import React, { useCallback, useEffect, useRef } from 'react';
import { PageHeader, useToast, Alert, Button } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import SegmentedControl from '../../components/common/SegmentedControl';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useMyPageStrings } from './myPageStrings';
import { useMyPageScreen } from './useMyPageScreen';
import { useDoctorPageLeads } from './useDoctorPage';
import { LEADS_PAGE_SIZE, everPublished, orderedSections } from './MyPage.shared';
import { MyPageSkeleton } from './MyPageSkeleton';
import { StatusCard, TemplatePicker, ThemeCard, SlugField, SectionList, PageBanners } from './parts/rail';
import { EditorCard } from './parts/common';
import PublishDialog from './parts/PublishDialog';
import { LeadsTable } from './parts/Leads';
import SectionEditor from './editors/SectionEditor';

// ─────────────────────────────────────────────────────────────────────────────
// «صفحهٔ من» — desktop (canonical). Design §12: a rail (status, template,
// theme, slug, sections) beside EVERY section's editor, stacked in the page's
// order — the rail's «بخش‌ها» jumps to one and marks it active. The real
// preview is the «پیش‌نمایش» button — the true renderer in a new tab — so no
// in-app approximation is drawn. Render-only: behaviour is useMyPageScreen.
// ─────────────────────────────────────────────────────────────────────────────

export default function MyPageWeb() {
  const s = useMyPageStrings();
  const toast = useToast();
  const navigate = useAppNavigate();
  const notify = useCallback((kind, key) => toast[kind]?.(s(key)), [toast, s]);
  const screen = useMyPageScreen({ notify });
  const { page, loading, error, reload, cards, publishErrors, actions, tab, readOnly } = screen;
  const leads = useDoctorPageLeads({ active: tab === 'leads' && !!page, pageSize: LEADS_PAGE_SIZE });
  const selected = screen.section || 'hero';

  // A click in «بخش‌ها» selects the section AND brings its card into view —
  // also when it is already the selected one, so it is not an effect on `selected`.
  const scrollToSection = useCallback((kind) => {
    document.getElementById(`my-page-section-${kind}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);
  const focusSection = useCallback((kind) => {
    screen.selectSection(kind);
    scrollToSection(kind);
  }, [screen, scrollToSection]);
  // A deep link (?section=office) lands on its card once the page has loaded.
  const deepLinked = useRef(false);
  useEffect(() => {
    if (!page || deepLinked.current || tab !== 'builder') return;
    deepLinked.current = true;
    if (screen.section) scrollToSection(screen.section);
  }, [page, tab, screen.section, scrollToSection]);

  const tabs = (
    <SegmentedControl
      ariaLabel={s('myPage.tabs.label')}
      testId="my-page-tabs"
      value={tab}
      onChange={screen.selectTab}
      items={[
        { id: 'builder', label: s('myPage.tabs.builder') },
        { id: 'leads', label: s('myPage.tabs.leads') },
      ]}
    />
  );

  let body;
  if (loading && !page) {
    body = <MyPageSkeleton />;
  } else if (error && !page) {
    body = (
      <div data-testid="my-page" data-state="error">
        <ErrorState error={error} title={s('myPage.loadFailed')} onRetry={reload} />
      </div>
    );
  } else if (page && tab === 'leads') {
    body = <LeadsTable leads={leads} published={everPublished(page)} />;
  } else if (page) {
    const missingCouncil = (page.publishBlockers || []).some((b) => b.code === 'MISSING_COUNCIL_NUMBER');
    body = (
      <div data-testid="my-page" data-state="content" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'flex-start' }}>
        {/* Sticky, with its own scroll: «بخش‌ها» stays in reach however far down the editors the doctor is. */}
        <aside
          style={{
            flex: '1 1 17rem', maxWidth: '22rem', display: 'grid', gap: '1rem', minWidth: 0,
            position: 'sticky', top: '1rem', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto', overscrollBehavior: 'contain',
          }}
          aria-label={s('myPage.railLabel')}
        >
          <StatusCard
            page={page}
            cards={cards}
            onPreview={screen.preview}
            onPublishClick={screen.openPublish}
            onUnpublish={screen.unpublish}
          />
          {screen.blockedPreview ? (
            <Alert variant="info">
              <a href={screen.blockedPreview} target="_blank" rel="noopener noreferrer" style={{ color: C.navy }}>
                {s('myPage.statusCard.openPreview')}
              </a>
            </Alert>
          ) : null}
          {missingCouncil ? (
            <Button variant="outline" size="sm" onClick={() => navigate('profile', { tab: 'profile' })} data-testid="my-page-go-profile">
              {s('myPage.statusCard.addCouncil')}
            </Button>
          ) : null}
          <SlugField page={page} cards={cards} onSave={screen.saveSlug} readOnly={readOnly} />
          <TemplatePicker page={page} cards={cards} onChange={screen.changeTemplate} readOnly={readOnly} />
          <ThemeCard page={page} cards={cards} onChange={screen.changeTheme} readOnly={readOnly} />
          <SectionList
            page={page}
            cards={cards}
            selected={selected}
            onSelect={focusSection}
            onToggle={screen.toggleSection}
            onMove={screen.moveSections}
            readOnly={readOnly}
            publishErrors={publishErrors}
          />
        </aside>
        <div style={{ flex: '999 1 30rem', minWidth: 0, display: 'grid', gap: '1.25rem' }} data-testid="my-page-editors">
          {orderedSections(page.sections).map((sec) => (
            <EditorCard
              key={sec.kind}
              id={`my-page-section-${sec.kind}`}
              active={sec.kind === selected}
              title={s(`myPage.sections.kinds.${sec.kind}`)}
              testId={`my-page-editor-card-${sec.kind}`}
            >
              <SectionEditor
                page={page}
                kind={sec.kind}
                actions={actions}
                cards={cards}
                readOnly={readOnly}
                publishErrors={publishErrors}
              />
            </EditorCard>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full flex flex-col bg-background">
      <PageHeader title={s('nav.items.myPage')} sub={s('myPage.subtitle')} />
      <div style={{ padding: '1.5rem 2rem', display: 'grid', gap: '1.25rem', maxWidth: '90rem', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        {page ? <PageBanners page={page} /> : null}
        <div>{tabs}</div>
        {body}
      </div>
      {page ? (
        <PublishDialog
          open={screen.publishOpen}
          onClose={screen.closePublish}
          onPublish={screen.publish}
          busy={!!cards.publish?.saving}
          error={cards.publish?.error}
          isMobile={false}
        />
      ) : null}
    </div>
  );
}
